import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { showToast } from '../../../../components/Toast';
import { API_DOCS } from './RdaPage.api';
import './RdaPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoRda = 'APERTA' | 'EVASA' | 'ANNULLATA';

interface RdaItem {
  id: number;
  societaId: number;
  numero: number;
  anno: number;
  etichetta: string;
  stato: StatoRda;
  note: string | null;
  createdAt: string;
  createdBy: { id: number; nome: string } | null;
  _count: { righe: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_RDA: Record<StatoRda, { label: string; cls: string }> = {
  APERTA:    { label: 'Aperta',    cls: 'bop-badge-info' },
  EVASA:     { label: 'Evasa',     cls: 'bop-badge-success' },
  ANNULLATA: { label: 'Annullata', cls: 'bop-badge-danger' },
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderStato(stato: StatoRda): React.ReactNode {
  const cfg = STATO_RDA[stato] ?? { label: stato, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function RdaPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [stato, setStato] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<RdaItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/rda?societaId&stato -> {"success":true,"data":[{"id":"number","societaId":"number","numero":"number","anno":"number","etichetta":"string","stato":"APERTA|EVASA|ANNULLATA","note":"string|null","createdAt":"string","createdBy":{"id":"number","nome":"string"},"_count":{"righe":"number"}}]}
      const r = await apiFetch<{ success: boolean; data: RdaItem[] }>(`/api/rda${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento delle RDA'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Ricerca client-side su etichetta, note e creatore
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    return items.filter(r => {
      const testo = [
        r.etichetta || '',
        r.note || '',
        r.createdBy?.nome || '',
      ].join(' ').toLowerCase();
      return testo.includes(q);
    });
  }, [items, q]);

  const pulisciFiltri = () => {
    setStato('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="rda-lista">
        {/* Header — nessun bottone "Nuova": le RDA nascono dalle pagine Fabbisogni */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Richieste di Acquisto</h1>
          {can('acquisti.ordini_fornitori', 'edit') && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/passivo/ordini/nuovi')}
              title="Componi gli ordini fornitore dalle righe RDA da ordinare"
            >
              Ordina a fornitore
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar rda-lista__filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input rda-lista__search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="RDA, note, creatore..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="APERTA">Aperta</option>
              <option value="EVASA">Evasa</option>
              <option value="ANNULLATA">Annullata</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare le RDA</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>RDA</th>
                  <th className="bop-text-right">Righe</th>
                  <th>Stato</th>
                  <th>Creata da</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          Nessuna RDA trovata con i filtri attuali.
                          <br />
                          Le RDA si generano dai Fabbisogni; da qui si compongono gli ordini fornitore.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(r => (
                    <tr key={r.id} onClick={() => navigate(`/commerciale/passivo/rda/${r.id}`)}>
                      <td><span className="rda-lista__etichetta">{r.etichetta}</span></td>
                      <td className="bop-text-right">{r._count?.righe ?? 0}</td>
                      <td>{renderStato(r.stato)}</td>
                      <td>{r.createdBy?.nome || <span className="bop-text-muted">—</span>}</td>
                      <td>{formatDateIT(r.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
